import React, { useState, useEffect } from "react";
import { Row, Col, Input, Modal, Button, AutoComplete } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useUser } from "../contexts/UserContext";
import CandidateTestCard from "../components/CandidateTestCard";
import TestFilter from "../components/TestFilter";
import api from "../services/api";
import { toast } from "react-toastify";
import { useUsageStatus } from "../hooks/useUsageStatus";
import { useCart } from "../contexts/CartContext";
import DuplicateAssessmentModal from "../components/DuplicateAssessmentModal";
import magnifying from "../images/icons/Magnifying.svg";

interface Test {
  id: number;
  name: string;
  price: number;
  industry_name: string;
  industry_color: string;
  industry_id: number;
  software_name: string;
  software_id: number;
  question_count: number;
  time_limit: number;
  passing_grade: number;
  description?: string;
}

interface SubscriptionStatus {
  plan: {
    name: string;
    price: string;
    interval: string;
    candidate_limit: number;
    candidate_range: string;
    is_trial: boolean;
  } | null;
  trial_used: boolean;
  status: string;
  end_date: string | null;
  trial_end: string | null;
  is_active: boolean;
  plan_type?: string;
}

interface FilterValues {
  industries: number[];
  software: number[];
  questionRange: [number, number];
  timeLimit: {
    timed: boolean;
    timeRange: [number, number];
    noTimeLimit: boolean;
  };
}

const BusinessAssessment: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAdmin } = useUser();
  const [tests, setTests] = useState<Test[]>([]);
  const [allTests, setAllTests] = useState<Test[]>([]);
  const [filteredTests, setFilteredTests] = useState<Test[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [maxQuestions, setMaxQuestions] = useState(50);
  const [maxTimeLimit, setMaxTimeLimit] = useState(90);
  const [sendEmailModal, setSendEmailModal] = useState<{
    open: boolean;
    test: Test | null;
  }>({ open: false, test: null });
  const [emailForm, setEmailForm] = useState({ email: "", body: "" });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [userEmails, setUserEmails] = useState<string[]>([]);
  const [invitationLink, setInvitationLink] = useState<string>("");
  const [duplicateWarning, setDuplicateWarning] = useState<any>(null);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [pendingInvitation, setPendingInvitation] = useState<any>(null);
  const { isInCart } = useCart();

  // Usage status for checking limits
  const { usage, refresh: refreshUsage } = useUsageStatus();

  // Subscription status
  const [subscription, setSubscription] = useState<SubscriptionStatus | null>(null);

  // Fetch subscription status
  useEffect(() => {
    const fetchSubscriptionStatus = async () => {
      if (!user || !isAdmin()) {
        return;
      }

      try {
        const response = await api.get('subscriptions/me/');
        setSubscription(response.data);
      } catch (error) {
        console.error('Error fetching subscription status:', error);
      }
    };

    fetchSubscriptionStatus();
  }, [user, isAdmin]);

  useEffect(() => {
    loadTests();
    loadUserEmails();
  }, []);

  // Debug subscription status
  useEffect(() => {
    console.log('Subscription status changed:', subscription);
  }, [subscription]);

  useEffect(() => {
    setTests(filteredTests);
  }, [filteredTests]);

  // Check if user is admin, if not redirect
  useEffect(() => {
    if (!isAdmin()) {
      navigate('/dashboard');
      return;
    }
  }, [user, isAdmin, navigate]);

  const loadTests = async () => {
    try {
      setLoading(true);
      const response = await api.get('browse-tests/');
      const testsWithDescription = response.data.map((test: any) => ({
        ...test,
        description: test.description || "No description available.",
      }));

      // Calculate max values for filters
      const maxQ = Math.max(...testsWithDescription.map((test: Test) => test.question_count));
      const maxT = Math.max(...testsWithDescription.map((test: Test) => test.time_limit).filter((t: number) => t > 0));

      setMaxQuestions(maxQ);
      setMaxTimeLimit(maxT);
      setAllTests(testsWithDescription);
      setFilteredTests(testsWithDescription);
      setTests(testsWithDescription);
    } catch (error: any) {
      console.error('Failed to load assessments:', error);
      toast.error('Failed to load assessments. Please try again.');
      setAllTests([]);
      setTests([]);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = (filters?: FilterValues, search?: string) => {
    let filtered = [...allTests];
    const currentSearch = search !== undefined ? search : searchTerm;

    // Filter by search term (test name)
    if (currentSearch.trim()) {
      filtered = filtered.filter(test =>
        test.name.toLowerCase().includes(currentSearch.toLowerCase())
      );
    }

    // Filter by industries
    if (filters?.industries && filters.industries.length > 0) {
      filtered = filtered.filter(test =>
        filters.industries.includes(test.industry_id)
      );
    }

    // Filter by software
    if (filters?.software && filters.software.length > 0) {
      filtered = filtered.filter(test =>
        test.software_id && filters.software.includes(test.software_id)
      );
    }

    // Filter by question count range
    if (filters?.questionRange) {
      filtered = filtered.filter(test =>
        test.question_count >= filters.questionRange[0] &&
        test.question_count <= filters.questionRange[1]
      );
    }

    // Filter by time limit
    if (filters?.timeLimit) {
      if (filters.timeLimit.timed && !filters.timeLimit.noTimeLimit) {
        // Only timed tests
        filtered = filtered.filter(test =>
          test.time_limit > 0 &&
          test.time_limit >= filters.timeLimit.timeRange[0] &&
          test.time_limit <= filters.timeLimit.timeRange[1]
        );
      } else if (!filters.timeLimit.timed && filters.timeLimit.noTimeLimit) {
        // Only no time limit tests
        filtered = filtered.filter(test => test.time_limit === 0);
      } else if (filters.timeLimit.timed && filters.timeLimit.noTimeLimit) {
        // Both timed and no time limit
        filtered = filtered.filter(test =>
          test.time_limit === 0 ||
          (test.time_limit >= filters.timeLimit.timeRange[0] &&
            test.time_limit <= filters.timeLimit.timeRange[1])
        );
      }
    }

    setFilteredTests(filtered);
  };

  const handleFilterChange = (filters: FilterValues) => {
    applyFilters(filters);
  };

  const handleClearAllFilters = () => {
    // Reset filters to default state
    const defaultFilters: FilterValues = {
      industries: [],
      software: [],
      questionRange: [0, maxQuestions || 50],
      timeLimit: {
        timed: false,
        timeRange: [10, maxTimeLimit || 90],
        noTimeLimit: false,
      }
    };
    applyFilters(defaultFilters);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    applyFilters(undefined, value);
  };

  const handleViewQuestions = (test: Test) => {
    // Navigate to test builder in view mode (industry_id and test_id)
    navigate(`/test-builder/${test.industry_id}/${test.id}`);
  };

  const handleViewApplicants = (test: Test) => {
    // Navigate to view candidates page with test filter
    navigate(`/view-candidates?test_id=${test.id}&test_name=${encodeURIComponent(test.name)}`);
  };

  const handleSendTo = (test: Test) => {
    console.log('handleSendTo called with test:', test);
    console.log('Current usage status:', usage);

    // Check if limit is exceeded first (skip for super admins)
    if (!user?.is_super_admin) {
      if (usage?.exceeded) {
        toast.error(`Usage limit reached! You have used ${usage.current_usage}/${usage.limit} candidates. Please upgrade your plan to send more invitations.`);
      } else if (usage?.at_risk) {
        toast.warning(`You are approaching your invitation limit (${usage.current_usage}/${usage.limit}). Consider upgrading your plan.`);
      }
    }

    console.log('Opening send email modal...');
    setSendEmailModal({ open: true, test });
  };

  const handleSendEmail = async () => {
    if (!emailForm.email.trim()) {
      toast.error("Email address is required");
      return;
    }
    if (!sendEmailModal.test) return;

    // Check usage limits before sending (skip for super admins)
    if (!user?.is_super_admin) {
      const isNewCandidate = !usage?.unique_candidates?.includes(emailForm.email.toLowerCase());
      if (usage?.exceeded && isNewCandidate) {
        toast.error("Usage limit reached! You cannot invite new candidates this month. Please upgrade your plan.");
        return;
      }
    }

    // NEW: Check for duplicate assessments
    try {
      const duplicateResponse = await api.post("check-duplicate-assessment/", {
        email: emailForm.email,
        test_id: sendEmailModal.test.id,
      });

      if (duplicateResponse.data.has_duplicate) {
        setDuplicateWarning(duplicateResponse.data);
        setPendingInvitation({
          email: emailForm.email,
          test_id: sendEmailModal.test.id,
          body: emailForm.body,
        });
        setShowDuplicateModal(true);
        return; // Stop here and show modal
      }
    } catch (err) {
      console.error("Error checking duplicates:", err);
      // Continue with invitation if check fails
    }

    // Proceed with normal invitation
    await sendInvitation();
  };

  // New function to handle the actual invitation sending
  const sendInvitation = async () => {
    if (!pendingInvitation && !sendEmailModal.test) return;

    const invitationData = pendingInvitation || {
      email: emailForm.email,
      test_id: sendEmailModal.test?.id || 0,
      body: emailForm.body,
    };

    try {
      setSendingEmail(true);
      const response = await api.post("send-test-invitation/", invitationData);

      toast.success("Assessment assigned and invitation sent successfully!");

      if (response.data && response.data.invitation_token) {
        const invitationUrl = `${window.location.origin}/invitation?token=${response.data.invitation_token}`;
        setInvitationLink(invitationUrl);
      }

      // Clean up
      setShowDuplicateModal(false);
      setDuplicateWarning(null);
      setPendingInvitation(null);

      refreshUsage();
    } catch (err: any) {
      console.error("Failed to send invitation:", err);
      let errorMessage = "Failed to send invitation. Please try again.";

      if (err.response?.status === 404) {
        errorMessage = "User with this email address does not exist. Please check the email or register the user first.";
      } else if (err.response?.status === 400) {
        const responseError = err.response?.data?.error;
        if (responseError?.includes("already assigned")) {
          errorMessage = "This test is already assigned to this user.";
        } else {
          errorMessage = responseError || errorMessage;
        }
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      }

      toast.error(errorMessage);
    } finally {
      setSendingEmail(false);
    }
  };

  const handleModalClose = () => {
    setSendEmailModal({ open: false, test: null });
    setEmailForm({ email: "", body: "" });
    setInvitationLink("");
  };

  const loadUserEmails = async () => {
    try {
      const response = await api.get("users/emails/");
      setUserEmails(response.data);
    } catch (err) {
      console.error("Failed to load user emails:", err);
    }
  };

  // Don't render anything if not admin
  if (!isAdmin()) {
    return null;
  }

  return (
    <div style={bg}>
      {/* Main content with filter sidebar */}
      <Row gutter={[32, 0]}>
        {/* Filter Sidebar */}
        <Col xs={24} lg={6}>
          <TestFilter
            onFilterApply={handleFilterChange}
            maxQuestions={maxQuestions}
            maxTimeLimit={maxTimeLimit}
          />
        </Col>

        {/* Tests Grid */}
        <Col xs={24} lg={18}>
          {/* Search Input */}
          <div style={searchContainer} className="input-form">
            <Input
              placeholder="Search assessments by name"
              value={searchTerm}
              onChange={handleSearchChange}
              suffix={<img src={magnifying} style={{ height: 15, width: 15 }} />}
              style={searchInput}
              size="large"
            />
          </div>

          {/* Test Cards Grid */}
          <Row gutter={[24, 24]} style={cardGrid}>
            {loading ? (
              <Col span={24}>
                <div style={loadingContainer}>
                  Loading assessments...
                </div>
              </Col>
            ) : tests.length === 0 ? (
              <Col span={24}>
                <div style={{
                  textAlign: 'center',
                  padding: '60px 20px',
                  color: '#666',
                }}>
                  <div style={{
                    fontSize: '18px',
                    fontWeight: 500,
                    color: '#1a1a1a',
                    marginBottom: '8px',
                  }}>
                    {searchTerm ? "No assessments found" : "No assessments match your filters"}
                  </div>
                  <div style={{
                    fontSize: '14px',
                    color: '#666',
                    marginBottom: '20px',
                  }}>
                    {searchTerm
                      ? `No assessments found for "${searchTerm}". Try adjusting your search term or filters.`
                      : "Try adjusting your filter criteria to find the assessments you're looking for."
                    }
                  </div>
                  <Button
                    type="primary"
                    onClick={handleClearAllFilters}
                    style={{
                      background: '#2563eb',
                      borderColor: '#2563eb',
                      borderRadius: '6px',
                      fontWeight: 500,
                    }}
                  >
                    Clear all filters
                  </Button>
                </div>
              </Col>
            ) : (
              tests.map(test => (
                <Col key={test.id} xs={24} sm={12} lg={8}>
                  <CandidateTestCard
                    id={test.id}
                    title={test.name}
                    price={test.price}
                    questionCount={test.question_count}
                    timeLimit={test.time_limit > 0 ? `${test.time_limit}` : "No time limit"}
                    description={test.description || "No description available."}
                    industryTags={[test.industry_name]}
                    industryColor={test.industry_color}
                    onViewResults={() => handleViewQuestions(test)}
                    onViewApplicants={() => handleViewApplicants(test)}
                    onDownloadCertificate={() => handleSendTo(test)}
                    isAdminView={true}
                    inCart={isInCart(test.id)}
                    showCart={false}
                  />
                </Col>
              ))
            )}
          </Row>
        </Col>
      </Row>

      {/* Send Email Modal */}
      <Modal
        open={sendEmailModal.open}
        footer={
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            width: '100%',
            minHeight: '40px'
          }}>
            <div style={{
              fontSize: '0.85em',
              color: '#666',
              flex: 1,
              textAlign: 'left',
              alignSelf: 'flex-start'
            }}>
              {subscription?.plan?.is_trial && subscription?.status === "trialing" ? (
                usage ? (
                  <div>
                    <div>{Math.max(0, usage.limit - usage.current_usage)} candidates remaining in trial</div>
                    {usage?.exceeded && (
                      <div style={{ color: '#ff4d4f', fontSize: '0.75em', marginTop: 4 }}>
                        Trial limit reached - upgrade to continue
                      </div>
                    )}
                  </div>
                ) : (
                  "Loading usage..."
                )
              ) : subscription?.status === "active" ? (
                usage ? (
                  <div>
                    <div>{Math.max(0, usage.limit - usage.current_usage)} candidates remaining this month</div>
                    {usage?.exceeded && (
                      <div style={{ color: '#ff4d4f', fontSize: '0.75em', marginTop: 4 }}>
                        Monthly limit reached
                      </div>
                    )}
                  </div>
                ) : (
                  "Loading usage..."
                )
              ) : subscription ? (
                usage ? (
                  <div>
                    <div>{Math.max(0, usage.limit - usage.current_usage)} candidates remaining</div>
                    {usage?.exceeded && (
                      <div style={{ color: '#ff4d4f', fontSize: '0.75em', marginTop: 4 }}>
                        Usage limit reached
                      </div>
                    )}
                  </div>
                ) : (
                  "Loading usage..."
                )
              ) : (
                "No active subscription"
              )}
            </div>
            <div style={{
              display: 'flex',
              gap: 8,
              alignSelf: 'flex-end'
            }}>
              <Button onClick={handleModalClose}>
                {invitationLink ? "Close" : "Cancel"}
              </Button>
              <Button
                type="primary"
                loading={sendingEmail}
                onClick={handleSendEmail}
                disabled={
                  !!invitationLink ||
                  sendingEmail ||
                  !emailForm.email.trim() ||
                  (usage?.exceeded &&
                    !usage?.unique_candidates?.includes(emailForm.email.toLowerCase()))
                }
                style={{
                  backgroundColor: "#2f3349",
                  borderColor: "#2f3349",
                }}
              >
                Send
              </Button>
            </div>
          </div>
        }
        onCancel={handleModalClose}
        centered
        width={500}
      >
        <div style={{ padding: "20px 0" }}>
          <h3 style={{ textAlign: "center" }}>{sendEmailModal.test?.name}</h3>
          <div style={{ marginBottom: 16, textAlign: "center", color: "#666" }}>
            Send to...
          </div>

          <div style={{ marginBottom: 16 }}>
            <AutoComplete
              style={{ width: "100%" }}
              placeholder="Email address"
              value={emailForm.email}
              onChange={(value) =>
                setEmailForm((prev) => ({ ...prev, email: value }))
              }
              options={userEmails.map((email) => ({
                value: email,
                label: email,
              }))}
              filterOption={(inputValue, option) =>
                option?.value
                  ?.toLowerCase()
                  .includes(inputValue.toLowerCase()) || false
              }
              disabled={!!invitationLink}
            />
          </div>

          {invitationLink && (
            <div
              style={{
                padding: "16px",
                backgroundColor: "#f6f8fa",
                border: "1px solid #d0d7de",
                borderRadius: 8,
                marginTop: 16,
              }}
            >
              <div
                style={{ marginBottom: 8, fontWeight: 600, color: "#24292f" }}
              >
                Invitation Link:
              </div>
              <div
                style={{
                  padding: "8px 12px",
                  backgroundColor: "#ffffff",
                  border: "1px solid #d0d7de",
                  borderRadius: 6,
                  fontSize: "14px",
                  fontFamily: "monospace",
                  wordBreak: "break-all",
                  cursor: "pointer",
                }}
                onClick={() => {
                  navigator.clipboard.writeText(invitationLink);
                  toast.success("Link copied to clipboard!");
                }}
              >
                {invitationLink}
              </div>
              <div
                style={{
                  marginTop: 8,
                  fontSize: "12px",
                  color: "#656d76",
                  textAlign: "center",
                }}
              >
                Click to copy link to clipboard
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* Duplicate Assessment Warning Modal */}
      <DuplicateAssessmentModal
        visible={showDuplicateModal}
        warning={duplicateWarning}
        onProceed={sendInvitation}
        onCancel={() => {
          setShowDuplicateModal(false);
          setDuplicateWarning(null);
          setPendingInvitation(null);
        }}
        loading={sendingEmail}
      />
    </div>
  );
};

// Styles
const bg: React.CSSProperties = {
  minHeight: "100vh",
  padding: "20px 0px",
};

const searchContainer: React.CSSProperties = {
  marginBottom: 32,
  display: "flex",
};

const searchInput: React.CSSProperties = {
  borderRadius: 22,
  backgroundColor: "#E5EBFA",
  maxWidth: "600px",
  width: "100%",
  border: "none",
  fontSize: 14,
  padding: "7px 15px"
};

const cardGrid: React.CSSProperties = {
  marginBottom: "40px",
};

const loadingContainer: React.CSSProperties = {
  textAlign: "center",
  padding: "3rem",
  color: "#666",
  fontSize: "16px",
};

export default BusinessAssessment;